<template>
  <div>
    <div class="panel m-3">
      <PanelHeader :panel-header="name || 'Kasse'" />
      <div class="container pt-3 pb-3">
        <div class="stammdaten">
          <div class="stammdaten-facts">
            <div class="kv-columns">
              <div class="kv-grid">
                <span>Kassennummer:</span>
                <span class="has-text-weight-bold">{{ num }}</span>
                <template v-if="gegenkonto">
                  <span>Gegenkonto (Export):</span>
                  <span class="has-text-weight-bold">{{ gegenkonto }}</span>
                </template>
                <span>Darf das Konto ins Minus gehen?</span>
                <span>{{ allowNegativeValue ? "Ja" : "Nein" }}</span>
                <template v-if="managerName">
                  <span>Verantwortlicher:</span>
                  <span>
                    <a @click="openManager">{{ managerName }}</a>
                  </span>
                </template>
                <template v-if="chosenGroup">
                  <span>Gruppe:</span>
                  <span>
                    <a @click="openGroup">
                      {{ chosenGroup.attributes.group_name }}
                    </a>
                  </span>
                </template>
              </div>
              <div v-if="accountList.length > 0">
                <p class="has-text-weight-bold">
                  Konten
                </p>
                <p
                  v-for="acc in accountList"
                  :key="acc.id"
                >
                  {{ acc.name + " (" + acc.token + ")" }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <section class="mx-5 my-2">
      <div
        class="buttons"
        style="display: flex"
      >
        <b-button
          class="is-primary"
          tag="router-link"
          to="/balance_overview"
        >
          Zurück
        </b-button>
        <b-button
          class="is-danger"
          style="margin-left: auto"
          @click="isDeleteCheck = true"
        >
          Löschen
        </b-button>
        <b-button
          class="is-primary"
          @click="triggerEdit"
        >
          Bearbeiten
        </b-button>
      </div>
    </section>

    <div
      v-if="budgetList.length > 0"
      class="panel m-3"
    >
      <PanelHeader panel-header="Budgets" />
      <div class="container pt-3 pb-3">
        <b-table
          :data="budgetList"
          default-sort="attributes.budgetNumber"
          narrowed
          striped
        >
          <b-table-column
            v-slot="props"
            field="attributes.budgetNumber"
            label="Nummer"
            cell-class="has-text-left"
            sortable
          >
            {{ props.row.attributes.budgetNumber }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            field="attributes.name"
            label="Name"
            cell-class="has-text-left"
            sortable
          >
            {{ props.row.attributes.name }}
          </b-table-column>
          <b-table-column
            v-slot="props"
            label="Art"
            cell-class="has-text-left"
          >
            {{ props.row.attributes.childSpecific
              ? "Kind-spezifisch" : "Kind-unspezifisch" }}
          </b-table-column>
          <b-table-column
            v-if="hasBudgetDescriptions"
            v-slot="props"
            label="Hinweis"
            cell-class="has-text-left"
          >
            {{ props.row.attributes.description }}
          </b-table-column>
        </b-table>
      </div>
    </div>

    <div
      v-if="clientRows.length > 0 || staffRows.length > 0"
      class="panel m-3"
    >
      <PanelHeader panel-header="Klienten und Mitarbeiter" />
      <div class="container pt-3 pb-3">
        <div class="member-columns">
          <div
            v-if="clientRows.length > 0"
            class="member-column"
          >
            <b-table
              class="member-table"
              :data="clientRows"
              narrowed
              striped
              hoverable
              @click="openClient"
            >
              <b-table-column
                v-slot="props"
                label="Klienten"
                cell-class="has-text-left"
              >
                {{ props.row.attributes.first_name }}
                {{ props.row.attributes.last_name }}
              </b-table-column>
            </b-table>
          </div>
          <div
            v-if="staffRows.length > 0"
            class="member-column"
          >
            <b-table
              class="member-table"
              :data="staffRows"
              narrowed
              striped
              hoverable
              @click="openUser"
            >
              <b-table-column
                v-slot="props"
                label="Mitarbeiter"
                cell-class="has-text-left"
              >
                {{ props.row.attributes.dependency.attributes.first_name }}
                {{ props.row.attributes.dependency.attributes.last_name }}
              </b-table-column>
            </b-table>
          </div>
        </div>
      </div>
    </div>

    <ModalComponent
      v-if="isDeleteCheck && balanceGroup"
      :item="name"
      :is-delete-check="isDeleteCheck"
      :is-loading="isLoading"
      :cancel="() => isDeleteCheck = false"
      :submit="submitDelete"
    />
  </div>
</template>

<script>
import Parse from "parse/dist/parse.min.js";
import PanelHeader from "@/components/helperComponents/PanelHeader";
import ModalComponent from "@/components/helperComponents/ModalComponent";
import { negativeFeedBack, positiveFeedBack } from "@/lib/Toast";

export default {
	name: "ViewBalanceGroup",
	components: {
		ModalComponent,
		PanelHeader
	},
	data() {
		return {
			name: "",
			manager: undefined,
			num: undefined,
			gegenkonto: "",
			description: "",
			accountList: [],
			budgetList: [],
			chosenClients: [],
			chosenGroup: undefined,
			balanceGroup: undefined,
			chosenUsers: [],
			allowNegativeValue: false,
			isDeleteCheck: false,
			isLoading: false,
		};
	},
	computed: {
		managerName() {
			const meta = this.manager && this.manager.attributes.dependency;
			if (!meta || !meta.attributes || !meta.attributes.first_name) {
				return "";
			}
			return `${meta.attributes.first_name} ${meta.attributes.last_name}`;
		},
		/** Only clients whose pointer data actually resolved. */
		clientRows() {
			return this.chosenClients.filter(client =>
				client && client.attributes && client.attributes.first_name);
		},
		/** Only users whose profile pointer actually resolved. */
		staffRows() {
			return this.chosenUsers.filter(user => {
				const meta = user && user.attributes.dependency;
				return !!(meta && meta.attributes && meta.attributes.first_name);
			});
		},
		hasBudgetDescriptions() {
			return this.budgetList.some(budget => {
				const description = budget.attributes.description;
				return !!(description && description.trim());
			});
		}
	},
	async created() {
		let balanceGroup = this.$route.params.balanceGroup;
		// Reached via browser navigation (back/forward, reload, deep
		// link): the in-memory object is gone, load it by the URL id.
		if (!balanceGroup && this.$route.params.id) {
			try {
				balanceGroup = await new Parse.Query("BalanceGroup")
					.include(
						[ "budgets", "entities", "users.dependency",
							"manager.dependency" ])
					.get(this.$route.params.id);
			}
			catch (e) {
				console.error(e);
			}
		}
		if (!balanceGroup) {
			return await this.$router.push("/balance_overview");
		}
		this.balanceGroup = balanceGroup;
		this.name = this.balanceGroup.attributes.name;
		this.description = this.balanceGroup.attributes.description;
		this.manager = this.balanceGroup.attributes.manager;
		this.num = +this.balanceGroup.attributes.balanceNumber;
		this.accountList =
			this.balanceGroup.attributes.accounts.filter(acc => !acc.isCash);
		this.budgetList = this.balanceGroup.attributes.budgets;
		const entities = this.balanceGroup.attributes.entities.slice();
		this.chosenGroup = entities[entities.length - 1];
		entities.pop();
		this.chosenClients = entities;
		this.chosenUsers = this.balanceGroup.attributes.users;
		this.allowNegativeValue =
			this.balanceGroup.attributes.allowNegativeAccountBalance;
		const gegenkonto = this.balanceGroup.attributes.gegenkonto;
		this.gegenkonto =
			String(gegenkonto) === "undefined" ? "" : String(gegenkonto);
	},
	methods: {
		guardSelection() {
			// A click that ends a drag-selection selects text, it should
			// not navigate.
			const selection = window.getSelection && window.getSelection();
			return !!(selection && selection.toString().length > 0);
		},
		openManager() {
			const meta = this.manager && this.manager.attributes.dependency;
			if (!meta) return;
			this.$router.push({ name: "ViewUser", params: { id: meta.id } });
		},
		openGroup() {
			if (!this.chosenGroup) return;
			this.$router.push(
				{ name: "ViewGroup", params: { id: this.chosenGroup.id } });
		},
		openClient(client) {
			if (this.guardSelection()) return;
			this.$router.push(
				{ name: "ViewClient", params: { id: client.id } });
		},
		openUser(user) {
			if (this.guardSelection()) return;
			const meta = user.attributes.dependency;
			this.$router.push({ name: "ViewUser", params: { id: meta.id } });
		},
		triggerEdit() {
			this.$router.push({
				name: "AddBalanceGroup",
				params: { balanceGroup: this.balanceGroup }
			});
		},
		async submitDelete() {
			this.isLoading = true;
			try {
				this.balanceGroup.set("deleted", true);
				const budgets = this.balanceGroup.attributes.budgets || [];
				budgets.forEach(budget => budget.set("deleted", true));
				await Parse.Object.saveAll([ this.balanceGroup, ...budgets ]);
				this.isDeleteCheck = false;
				await this.$router.push("/balance_overview");
				positiveFeedBack(this.name + " gelöscht!");
			}
			catch (e) {
				console.error(e);
				negativeFeedBack("Kasse nicht gelöscht!");
			}
			finally {
				this.isLoading = false;
			}
		}
	},
};
</script>

<style scoped>
.stammdaten {
	display: flex;
	align-items: flex-start;
	gap: 1.5rem;
	padding-left: 1.5rem;
}

.stammdaten-facts {
	text-align: left;
	min-width: 0;
}

.stammdaten-facts p {
	margin-bottom: 0.25rem;
}

.kv-columns {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 0.25rem 3.5rem;
}

.kv-grid {
	display: grid;
	/* Value column may shrink so long values wrap, not overflow. */
	grid-template-columns: max-content minmax(0, max-content);
	column-gap: 0.5rem;
	row-gap: 0.25rem;
	min-width: 0;
}

.member-columns {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 1.5rem;
	padding: 0 1.5rem;
}

.member-column {
	flex: 1;
	min-width: 16rem;
}

.member-table >>> tbody tr {
	cursor: pointer;
}

@media screen and (max-width: 768px) {
	.member-table {
		padding: 0 0.75rem;
	}
}
</style>
